<script setup lang="ts">

/**
 * TODO: Make a checkbox
 */

const props = defineProps({
  variant: {
    type: String,
    default: "",
    required: false,
    validator(value: string) {
      return [
        "",
        "success",
        "warning"
      ].includes(value);
    }
  },
  error: {
    type: [Array, String],
    default: () => []
  },
  currency: {
    type: Boolean
  },
  type: {
    type: String
  },
  selectOptions: {
    type: Array<{ label: string; value: string; }>
  },
  name: {
    type: String
  },
  placeholder: {
    type: String
  },
  mask: {
    type: String
  },
  modelValue: {
    type: String
  },
  disabled: {
    type: Boolean
  },
  required: {
    type: Boolean
  },
  inputClass: {
    type: String
  },
  uppercase: {
    type: String
  },
  readonly: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    validator(value: string) {
      return ["sm"].includes(value);
    }
  }
});

const messageError = computed(() => {
  return Array.isArray(props.error) ? [...props.error] : [...[props.error]];
});

const model = defineModel<string>();

const state = reactive({
  isPasswordVisible: false
});

const emit = defineEmits(["input", "click"]);

const currencyConfig = {
  masked: true,
  prefix: "R$ ",
  thousands: ".",
  decimal: ",",
  precision: 2
};
</script>

<template>
  <div
    class="group rounded-md !bg-transparent"
    :class="[{ 'border border-red-600 bg-red-600': error != '' && variant === '' },
            {
              // Disabled
              'cursor-pointer opacity-40': disabled,
            },
        ]"
  >
    <div class="relative flex items-stretch">
      <slot name="before"></slot>

      <template v-if="type === 'select'">
        <Select default-value="" v-model="model" class="focus:!hidden">
          <SelectTrigger
            class="relative peer z-10 focus:ring-offset-0 focus:!ring-transparent focus:border-none flex w-full h-auto p-4 font-semibold rounded-md !bg-brand-input text-sm placeholder-transparent shadow-sm !border-none focus:!outline-none focus:!border-transparent focus:!ring-0">
            <small
              class="after:opacity-0 after:content-['*'] after:px-px after:text-sm after:text-red-500 pointer-events-none absolute left-4 top-0.5 z-10 font-bold text-bzMenuText transition-all duration-500 ease-in-out peer-focus:left-4 peer-focus:top-0.5"
              :class="{
                'top-4 left-4 text-sm': !model,
                'text-xs': model
              }"
            >{{ placeholder }}</small>
            <SelectValue class="font-medium translate-y-1" />
          </SelectTrigger>
          <SelectContent class="!bg-brand-input !border-none !text-white">
            <SelectItem class="rounded-sm hover:!text-brand-primary hover:!bg-brand-card focus:!bg-brand-card focus:!text-brand-primary checked:hidden" v-for="option in selectOptions" :value="option.value" 
              :class="{
                'bg-brand-card text-brand-primary': model === option.value, // Adiciona classe quando o item for selecionado
              }"
            >
              {{ option.label }}
            </SelectItem>
          </SelectContent>
        </Select>
      </template>

      <template v-else>
        <div class="relative flex-1">
          <client-only v-if="currency">
            <input
              @input="emit('input', model)"
              v-money="currencyConfig"
              v-model="model"
              :type="type"
              :placeholder="placeholder"
              :name="name"
              :disabled="disabled"
              :readonly="readonly"
              class="peer z-10 block w-full rounded-md bg-brand-input p-4 text-sm placeholder-transparent shadow-sm focus:outline-none"
              :class="[
                inputClass,
                {
                  'input !border !border-yellow-500  autofill:border-yellow-600 autofill:border hover:!border' : variant === 'warning',

                  'input !border !border-green-500  autofill:border-green-600 autofill:border hover:!border' : variant === 'success',
                  // Placeholder
                  'pb-3 pt-5': placeholder,

                  // Disabled
                  'cursor-not-allowed': disabled,

                  uppercase: uppercase,

                  'pr-12': type === 'password'
                },
              ]"
            />
          </client-only>

          <client-only v-else>
            <input
              @input="emit('input', model)"
              :type="type === 'password' && state.isPasswordVisible ? 'text' : type"
              :inputmode="type === 'numeric-text' ? 'numeric' : 'text'"
              :placeholder="placeholder"
              :name="name"
              :readonly="readonly"
              class="peer z-10 block w-full rounded-md bg-brand-input p-4 text-sm placeholder-transparent shadow-sm focus:outline-none"
              :class="[
                inputClass,
                {
                  'input !border !border-yellow-500  autofill:border-yellow-600 autofill:border hover:!border' : variant === 'warning',

                  'input !border !border-green-500  autofill:border-green-600 autofill:border hover:!border' : variant === 'success',
                  // Placeholder
                  'pb-3 pt-5': placeholder,

                  // Disabled
                  'cursor-not-allowed': disabled,

                  uppercase: uppercase,

                  'pr-12': type === 'password'
                },
              ]"
              v-maska="{ eager: true }"
              :data-maska="mask"
              :disabled="disabled"
              v-model="model"
            />
          </client-only>

          <label
            :for="`input-${name}`"
            :class="[
              {
                // Disabled
                'cursor-not-allowed': disabled,
                'after:!opacity-100': required,
              },
                variant === 'success' ? 'text-green-500' :  '',
            ]"
            class="after:opacity-0 after:content-['*'] after:px-px after:text-sm after:text-red-500 pointer-events-none absolute left-4 top-0.5 z-10 text-xs font-bold text-bzMenuText transition-all duration-500 ease-in-out peer-placeholder-shown:left-4 peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-focus:left-4 peer-focus:top-0.5 peer-focus:text-xs"
          >
            {{ placeholder }}
          </label>
        </div>
      </template>

      <div class="flex flex-row justify-end items-center h-full absolute right-3 cursor-pointer" :class="{'right-6 z-50' : type === 'select' || type === 'number'}">
        <div v-if="messageError != ''" class="px-4">
          <p class="flex items-center justify-center peer text-white">
    <Icon name="fa6-solid:circle-exclamation" class="text-red-500" size="18" />
  </p>

          <div
            class=" whitespace-nowrap text-xs hidden rounded-md mr-2 peer-hover:block absolute z-[9999] -right-4 bg-red-600 p-2">
            <p class="z-[9999]"
              v-for="err in messageError"
              :key="err"
            >
              {{ err }}
            </p>
          </div>
        </div>


        <Icon
          :class="[
            {
                // Disabled
                'cursor-pointer': disabled,
              },
          ]"

          class="opacity-50 ml-auto"
          v-if="type === 'password'"
          @click="state.isPasswordVisible = !state.isPasswordVisible"
          :name="state.isPasswordVisible ? 'fa6-solid:eye' : 'fa6-solid:eye-slash'"
        />
        <Icon v-if="variant === 'warning'" name="material-symbols:exclamation-rounded" class=" text-yellow-500"
              size="23" />

        <div v-if="variant === 'success'">
          <svg class="w-4 fill-green-500" viewBox="0 0 512 512">
            <path
              d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-111 111-47-47c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l64 64c9.4 9.4 24.6 9.4 33.9 0L369 209z" />
          </svg>
        </div>

        <div
          :class="[
              {
                  // Disabled
                  'cursor-not-allowed': disabled,
                },
          ]">
          <slot name="icon"></slot>
        </div>
      </div>

    </div>
  </div>
</template>

<style>
.input:-webkit-autofill {
  @apply !border !border-green-500 !ring-1 !ring-green-500 hover:!border hover:!border-green-500 focus:!border;
}

input[type="date"] {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: textfield;
}

input::-webkit-date-and-time-value {
  text-align: left;
  opacity: 50%;
}

input:disabled::-webkit-date-and-time-value {
  opacity: 50%;
}
</style>